import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const WalletAndCoin = () => {
  const [activeTab, setActiveTab] = useState('wallet');
  const [isAddingFunds, setIsAddingFunds] = useState(false);
  const [amount, setAmount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('NGN');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sample data
  const walletData = {
    balance: 2000,
    transactions: [
      { id: 1, date: 'Apr 1', description: 'Event Payment', type: 'Debit', amount: '₦10,000', status: 'Success' },
      { id: 2, date: 'Mar 24', description: 'Wallet Top-Up', type: 'Credit', amount: '₦20,000', status: 'Success' },
      { id: 3, date: 'Mar 15', description: 'Refund - Event Cancelled', type: 'Credit', amount: '₦5,000', status: 'Success' },
      { id: 4, date: 'Mar 10', description: 'Event Payment', type: 'Debit', amount: '₦13,000', status: 'Success' }
    ]
  };

  const coinData = {
    balance: 20,
    history: [
      { id: 1, no: 1, date: 'Apr 1', eventName: 'Open Praise', activityType: 'Event Reviewed', coinsEarned: '10 coins' },
      { id: 2, no: 2, date: 'Apr 12', eventName: 'Referred a friend', activityType: 'Referred a friend', coinsEarned: '10 coins' },
      { id: 3, no: 3, date: 'Apr 15', eventName: 'Tech Conference', activityType: 'Ticket Purchase', coinsEarned: '5 coins' },
      { id: 4, no: 4, date: 'Apr 18', eventName: 'Live Concert', activityType: 'Event Reviewed', coinsEarned: '10 coins' }
    ]
  };

  const handleAddFunds = () => {
    setIsAddingFunds(true);
  };

  const handleSubmitFunds = (e) => {
    e.preventDefault();
    // Handle adding funds logic here
    setIsAddingFunds(false);
    setAmount('');
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 bg-[#F5F5F5] min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-8"
        >
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800">Wallet & Coin</h1>
          <p className="text-gray-500 mt-1 text-sm md:text-base">Manage your wallet balance and event coins</p>
        </motion.div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {['wallet', 'coin'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium relative capitalize ${activeTab === tab ? 'text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {tab === 'wallet' ? 'Wallet' : 'Coin'} Balance
              {activeTab === tab && (
                <motion.div 
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: activeTab === 'wallet' ? -20 : 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: activeTab === 'wallet' ? -20 : 20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Wallet Tab Content */}
            {activeTab === 'wallet' ? (
              <div className="space-y-6">
                {/* Wallet Balance */}
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
                >
                  <div className="md:flex md:justify-between md:items-start">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-800 mb-4">Wallet Balance</h2>
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                        </div>
                        <motion.p 
                          initial={{ scale: 0.9 }}
                          animate={{ scale: 1 }}
                          className="text-2xl md:text-3xl font-bold text-gray-900"
                        >
                          {selectedCurrency === 'NGN' ? '₦' : '$'}{walletData.balance.toLocaleString()}
                        </motion.p>
                      </div>
                    </div>
                    
                    <div className="mt-6 md:mt-0 flex flex-col md:flex-row md:items-center gap-3">
                      <div className="flex rounded-md shadow-sm">
                        <button 
                          onClick={() => setSelectedCurrency('NGN')} 
                          className={`px-4 py-2 text-sm font-medium rounded-l-md ${
                            selectedCurrency === 'NGN' 
                              ? 'bg-indigo-600 text-white' 
                              : 'bg-white text-gray-700 border border-gray-300'
                          }`}
                        >
                          NGN
                        </button>
                        <button 
                          onClick={() => setSelectedCurrency('USD')} 
                          className={`px-4 py-2 text-sm font-medium rounded-r-md ${
                            selectedCurrency === 'USD' 
                              ? 'bg-indigo-600 text-white' 
                              : 'bg-white text-gray-700 border border-gray-300'
                          }`}
                        >
                          USD
                        </button>
                      </div>
                      
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleAddFunds}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium"
                      >
                        Add Funds
                      </motion.button>
                    </div>
                  </div>

                  {/* Add Funds Form */}
                  <AnimatePresence>
                    {isAddingFunds && (
                      <motion.form
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        onSubmit={handleSubmitFunds}
                        className="mt-6 overflow-hidden border-t border-gray-100 pt-6"
                      >
                        <h3 className="text-md font-medium text-gray-800 mb-3">Add Funds to Wallet</h3>
                        <div className="flex flex-col md:flex-row md:items-end gap-3">
                          <div className="flex-1">
                            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                              Amount ({selectedCurrency === 'NGN' ? '₦' : '$'})
                            </label>
                            <div className="relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm">
                                  {selectedCurrency === 'NGN' ? '₦' : '$'}
                                </span>
                              </div>
                              <input
                                type="number"
                                id="amount"
                                name="amount"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-8 pr-12 sm:text-sm border-gray-300 rounded-md"
                                placeholder="0.00"
                                required
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              type="submit"
                              className="px-4 py-2 bg-green-600 text-white rounded-md text-sm whitespace-nowrap"
                            >
                              Add Funds
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              type="button"
                              onClick={() => setIsAddingFunds(false)}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm"
                            >
                              Cancel
                            </motion.button>
                          </div>
                        </div>
                        
                        {/* Payment Methods */}
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                          {['Credit Card', 'Bank Transfer', 'PayPal', 'Crypto'].map((method, index) => (
                            <div key={index} className="border border-gray-200 rounded-md p-3 text-center text-sm cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-colors">
                              {method}
                            </div>
                          ))}
                        </div>
                      </motion.form>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Transaction History */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
                >
                  <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-800">Transaction History</h2>
                    <button className="text-sm text-indigo-600 font-medium">Download Statement</button>
                  </div>
                  
                  {/* Desktop View */}
                  <div className="overflow-x-auto hidden md:block">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {walletData.transactions.map((transaction) => (
                          <motion.tr
                            key={transaction.id}
                            whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
                            className="transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.date}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{transaction.description}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.type}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{transaction.amount}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                {transaction.status}
                              </span>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Mobile View */}
                  <div className="md:hidden divide-y divide-gray-200">
                    {walletData.transactions.map((transaction) => (
                      <motion.div 
                        key={transaction.id}
                        className="p-4 hover:bg-gray-50"
                        whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm text-gray-500">{transaction.date}</span>
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            {transaction.status}
                          </span>
                        </div>
                        <h3 className="text-base font-medium text-gray-800 mb-1">{transaction.description}</h3>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">{transaction.type}</span>
                          <span className={`text-sm font-medium ${transaction.type === 'Credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {transaction.amount}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            ) : (
              /* Coin Tab Content */
              <div className="space-y-6">
                {/* Coin Balance */}
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
                >
                  <div className="md:flex md:justify-between md:items-center">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-800 mb-4">Coin Balance</h2>
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                          <svg className="w-8 h-8 text-yellow-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <motion.p 
                          initial={{ scale: 0.9 }}
                          animate={{ scale: 1 }}
                          className="text-2xl md:text-3xl font-bold text-gray-900"
                        >
                          {coinData.balance} coins
                        </motion.p>
                      </div>
                    </div>
                    
                    <div className="mt-6 md:mt-0 grid grid-cols-2 gap-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium flex items-center justify-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        Earn Coins
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium flex items-center justify-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
                        </svg>
                        Redeem
                      </motion.button>
                    </div>
                  </div>
                  
                  {/* Coin tiers */}
                  <div className="mt-6 border-t border-gray-100 pt-4">
                    <h3 className="text-sm font-medium text-gray-800 mb-3">Your Reward Tiers</h3>
                    <div className="bg-gray-100 h-4 rounded-full overflow-hidden">
                      <div className="bg-yellow-500 h-full" style={{ width: `${(coinData.balance / 100) * 100}%`, maxWidth: '100%' }} />
                    </div>
                    <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                      <span>0 coins</span>
                      <span>50 coins</span>
                      <span>100 coins</span>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      Earn {100 - coinData.balance} more coins to reach Gold tier and unlock exclusive rewards!
                    </p>
                  </div>
                </motion.div>

                {/* Engagement History */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
                >
                  <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-800">Engagement History</h2>
                    <select className="text-sm border border-gray-300 rounded-md p-1 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                      <option>All Activities</option>
                      <option>Event Reviewed</option>
                      <option>Referred a friend</option>
                      <option>Ticket Purchase</option>
                    </select>
                  </div>
                  
                  {/* Desktop View */}
                  <div className="overflow-x-auto hidden md:block">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coins Earned</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {coinData.history.map((item) => (
                          <motion.tr
                            key={item.id}
                            whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
                            className="transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.no}.</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.date}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.eventName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.activityType}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-yellow-600">{item.coinsEarned}</td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Mobile View */}
                  <div className="md:hidden divide-y divide-gray-200">
                    {coinData.history.map((item) => (
                      <motion.div 
                        key={item.id}
                        className="p-4 hover:bg-gray-50"
                        whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-700 mr-2">{item.no}.</span>
                            <span className="text-sm text-gray-500">{item.date}</span>
                          </div>
                          <span className="text-sm font-medium text-yellow-600">{item.coinsEarned}</span>
                        </div>
                        <h3 className="text-base font-medium text-gray-800 mb-1">{item.eventName}</h3>
                        <p className="text-sm text-gray-600">{item.activityType}</p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
                
                {/* Ways to Earn Coins */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
                >
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Ways to Earn More Coins</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { title: 'Review Events', coins: '10', icon: '✍️' },
                      { title: 'Refer Friends', coins: '20', icon: '👥' },
                      { title: 'Complete Profile', coins: '5', icon: '🏆' },
                    ].map((way, index) => (
                      <motion.div 
                        key={index}
                        whileHover={{ scale: 1.03 }}
                        className="p-4 border border-gray-200 rounded-lg"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-2xl">{way.icon}</span>
                          <span className="text-sm font-bold text-yellow-600">{way.coins} coins</span>
                        </div>
                        <h3 className="font-medium text-gray-800">{way.title}</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Earn coins when you {way.title.toLowerCase()}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default WalletAndCoin;